import React, { useState, useEffect } from "react";
import LeadAdvanceSearchForm from "./LeadAdvanceSearchForm";
import API from "services/axios";
import authHeader from "services/auth-header";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";

export default function LeadAdvanceFormLayout({ showModal, setSearchResult }) {
  const dispatch = useDispatch();
  const [requestList, setRequestList] = useState([
    {
      requestId: 1,
      leadId: "",
      leadName: "",
      externalLeadId: "",
      website: "",
      accountPayableContact: "",
      accountPayablePhone: "",
      accountPayableEmail: "",
      addressCompany: "",
      addressLine1: "",
      addressLine2: "",
      locationId: "",
      taxAccountId: "",
      paymentTermsId: "",
      salesPersonId: "",
      salesManager: "",
      poRequired: "",
      customField1: "",
      customField2: "",
      customField3: "",
    },
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  const nextRequest = () => {
    if (requestList.length > 0 && currentIndex < requestList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const previousRequest = () => {
    if (requestList.length > 0 && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const createNewRequest = () => {
    const maxId = requestList.reduce(
      (max, item) => Math.max(max, item.requestId),
      0
    );

    const newRequest = {
      requestId: maxId + 1,
      leadId: "",
      leadName: "",
      externalLeadId: "",
      website: "",
      accountPayableContact: "",
      accountPayablePhone: "",
      accountPayableEmail: "",
      addressCompany: "",
      addressLine1: "",
      addressLine2: "",
      locationId: "",
      taxAccountId: "",
      paymentTermsId: "",
      salesPersonId: "",
      salesManager: "",
      poRequired: "",
      customField1: "",
      customField2: "",
      customField3: "",
    };

    const updatedList = [...requestList, newRequest];
    setRequestList(updatedList);
    setCurrentIndex(updatedList.length - 1);
  };

  const duplicateRequest = () => {
    if (requestList.length === 0) return;

    const maxId = requestList.reduce(
      (max, item) => Math.max(max, item.requestId),
      0
    );

    const currentRequest = requestList[currentIndex];
    const duplicatedRequest = {
      ...currentRequest,
      requestId: maxId + 1,
    };

    const updatedList = [...requestList, duplicatedRequest];
    setRequestList(updatedList);
    setCurrentIndex(updatedList.length - 1);

    dispatch(
      setNotification({
        message: "Request duplicated successfully",
        type: "success",
      })
    );
  };

  const deleteRequest = () => {
    if (requestList.length <= 1) {
      dispatch(
        setNotification({
          message: "Cannot delete the last request",
          type: "warning",
        })
      );
      return;
    }

    const updatedList = requestList.filter(
      (_, index) => index !== currentIndex
    );
    setRequestList(updatedList);

    if (currentIndex >= updatedList.length) {
      setCurrentIndex(updatedList.length - 1);
    }

    dispatch(
      setNotification({
        message: "Request deleted successfully",
        type: "success",
      })
    );
  };

  const saveRequest = (fieldName, fieldValue) => {
    if (requestList.length === 0) return;

    const updatedList = [...requestList];
    updatedList[currentIndex] = {
      ...formData,
      [fieldName]: fieldValue,
      requestId: requestList[currentIndex].requestId,
    };
    setRequestList(updatedList);
  };

  const clearCurrentRequest = () => {
    if (requestList.length === 0) return;

    const clearedRequest = {
      requestId: requestList[currentIndex].requestId,
      leadId: "",
      leadName: "",
      externalLeadId: "",
      website: "",
      accountPayableContact: "",
      accountPayablePhone: "",
      accountPayableEmail: "",
      addressCompany: "",
      addressLine1: "",
      addressLine2: "",
      locationId: "",
      taxAccountId: "",
      paymentTermsId: "",
      salesPersonId: "",
      salesManager: "",
      poRequired: "",
      customField1: "",
      customField2: "",
      customField3: "",
    };

    const updatedList = [...requestList];
    updatedList[currentIndex] = clearedRequest;
    setRequestList(updatedList);
    setFormData(clearedRequest);
    dispatch(
      setNotification({
        message: "Request cleared successfully",
        type: "success",
      })
    );
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const { data } = await API.post("/lead/advance-search", requestList, {
        headers: authHeader(),
      });

      setSearchResult(data);
      setLoading(false);
      showModal(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  useEffect(() => {
    if (
      requestList.length > 0 &&
      currentIndex >= 0 &&
      currentIndex < requestList.length
    ) {
      setFormData(requestList[currentIndex]);
    }
  }, [currentIndex, requestList]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center gap-1">
        <h3 className="mt-0">Build A Lead Find</h3>
        <div className="d-flex align-items-center gap-1">
          <div className="me-3">
            Showing{" "}
            <span className="text-primary">
              {requestList?.length > 0 ? currentIndex + 1 : "0"}
            </span>{" "}
            of{" "}
            <span className="text-primary">
              {requestList?.length > 0 ? requestList.length : "0"}
            </span>{" "}
          </div>
          <button
            onClick={createNewRequest}
            disabled={loading}
            className="btn btn-success btn-sm p-0 px-1"
            title="Create New Request"
          >
            <i className="mdi mdi-plus fs-3 py-0 my-0"></i>
          </button>{" "}
          <button
            onClick={duplicateRequest}
            disabled={requestList?.length === 0 || loading}
            className="btn btn-warning btn-sm p-0 px-1"
            title="Duplicate Request"
          >
            <i className="mdi mdi-content-duplicate fs-3 py-0 my-0"></i>
          </button>
          <button
            onClick={clearCurrentRequest}
            disabled={requestList?.length === 0 || loading}
            className="btn btn-info btn-sm"
            style={{
              paddingTop: "0.4rem",
              paddingBottom: "0.4rem",
              paddingRight: "0.8rem",
              paddingLeft: "0.8rem",
            }}
            title="Clear Current Request"
          >
            <span className="fs-5 fw-bold">C</span>
          </button>
          <button
            onClick={deleteRequest}
            disabled={requestList?.length <= 1 || loading}
            className="btn btn-danger btn-sm p-0 px-1"
            title="Delete Request"
          >
            <i className="mdi mdi-delete fs-3 py-0 my-0"></i>
          </button>
          <div
            className="border-start  border-3 mx-2 "
            style={{ height: "35px", width: "1px" }}
          ></div>
          <div className="d-flex align-items-center gap-1">
            <button
              onClick={previousRequest}
              disabled={currentIndex <= 0 || loading}
              className="btn btn-success btn-sm p-0 px-1"
              title="Previous Request"
            >
              <i className="mdi mdi-arrow-left-bold fs-3 py-0 my-0"></i>
            </button>
            <button
              onClick={nextRequest}
              disabled={currentIndex >= requestList.length - 1 || loading}
              className="btn btn-success btn-sm p-0  px-1"
              title="Next Request"
            >
              <i className="mdi mdi-arrow-right-bold fs-3 py-0 my-0"></i>
            </button>
          </div>
        </div>
      </div>
      <hr className="mt-1" />

      {requestList.length > 0 && (
        <div>
          <LeadAdvanceSearchForm
            formData={formData}
            setFormData={setFormData}
            loading={loading}
            setLoading={setLoading}
            showModal={showModal}
            setSearchResult={setSearchResult}
            saveRequest={saveRequest}
            handleSearch={handleSearch}
          />
        </div>
      )}
    </div>
  );
}
